import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { BasePaymentModalComponent } from '../base-payment-modal/base-payment-modal.component';

/**
 * @component CashPaymentFormComponent
 * @description Component for handling cash payment form logic. Extends BasePaymentModalComponent.
 */
@Component({
	selector: 'app-cash-payment-form',
	templateUrl: './cash-payment-form.component.html',
	styleUrls: ['./cash-payment-form.component.scss']
})
export class CashPaymentFormComponent extends BasePaymentModalComponent implements OnInit {
	/**
	 * @constructor
	 * @param {FormBuilder} fb - The Angular FormBuilder service.
	 * @param {ChangeDetectorRef} cdr - The Angular ChangeDetectorRef service.
	 */
	constructor(protected fb: FormBuilder, protected cdr: ChangeDetectorRef) {
		super(fb, cdr);
	}

	/**
	 * @method ngOnInit
	 * @description Lifecycle hook that initializes the component.
	 * Sets up initial form state, updates the display total, initializes local lines,
	 * and subscribes to changes in the main lines FormArray.
	 */
	ngOnInit(): void {
		if (!this.lines) {
			console.error("CashPaymentFormComponent requires a lines FormArray input.");
			// Consider throwing an error or providing a default empty FormArray
			// For now, initialize localLines as empty if lines is missing
			this.localLines = this.fb.array([]);
			return;
		}
		// No need to subscribe to this.lines.valueChanges here if base class handles localLines
		this.initLocalLines(); // This initializes localLines and its valueChanges subscription
		// No need to call updateDisplayTotal() here, initLocalLines calls updateDisplayTotalLocal()
	}

	/**
	 * @method addCashLine
	 * @description Adds a new cash payment line to the form using the base class method.
	 * @param {boolean} [updateTotal=true] - Whether to update the display total after adding the line.
	 */
	addCashLine(updateTotal: boolean = true): void {
		this.addLineWithValidation(() => this.createEmptyLineGroup(), updateTotal);
	}

	/**
	 * @method onAmountInput
	 * @description Handles the input event for the amount field in a specific line.
	 * Delegates to the base class's local amount input handler.
	 * @param {object} event - The input event object containing originalEvent and value.
	 * @param {number} index - The index of the line being modified.
	 */
	onAmountInput(event: { originalEvent: Event, value: number | null }, index: number): void {
		this.onAmountInputLocal(event, index);
	}

	/**
	 * @method createEmptyLineGroup
	 * @description Creates a new, empty FormGroup representing a cash payment line.
	 * This implements the abstract method from the base class.
	 * @returns {FormGroup} A new FormGroup instance for a cash line.
	 */
	createEmptyLineGroup(): FormGroup {
		return this.fb.group({
			id: [null],
			date: [new Date(), Validators.required],
			amount: [null, [Validators.required, Validators.min(0.01)]],
			payment_type: [1] // Assuming 1 represents 'Cash'
		});
	}
}
